import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { FeaturesStatesService } from '@app/services/features-states.service';
import { FeatureState } from '@app/models';
import { AppSettings } from '@app/core/app-settings';
import { AppDataService } from '@app/services/store/app-data.service';

export type ConnectionStatus = 'disconnected' | 'connected' | 'reconnecting';

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private _hubConnection!: signalR.HubConnection;

  // Subject for connection status
  private _connectionStatus$ = new BehaviorSubject<ConnectionStatus>('disconnected');
  public connectionStatus$ = this._connectionStatus$.asObservable();

  // Subject for connectionId
  private _connectionId$ = new BehaviorSubject<string | null>(null);
  public connectionId$ = this._connectionId$.asObservable();

  constructor(
    private _featuresStatesService: FeaturesStatesService,
    private _appDataService: AppDataService,
  ) {}

  public startConnection(): void {
    this._hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${AppSettings.EnvUrl}datahub`, { withCredentials: false })
      .withAutomaticReconnect()
      .build();

    this._hubConnection
      .start()
      .then(() => {
        console.log('Connection started');
        this._connectionStatus$.next('connected');
        this._connectionId$.next(this._hubConnection.connectionId);
      })
      .catch((err) => {
        console.error('Error while starting connection: ' + err);
        this._connectionStatus$.next('disconnected');
      });

    this._hubConnection.onclose((error) => {
      console.error('Connection closed', error);
      this._connectionStatus$.next('disconnected');
      this._connectionId$.next(null);
    });

    this._hubConnection.onreconnecting((error) => {
      console.warn('Connection lost, reconnecting...', error);
      this._connectionStatus$.next('reconnecting');
    });

    this._hubConnection.onreconnected((connectionId) => {
      console.log('Reconnected successfully with connectionId:', connectionId);
      this._connectionStatus$.next('connected');
      this._connectionId$.next(connectionId);
    });
  }

  public registerOnServerEvents(): void {
    this._hubConnection.on('AlertsAndFeatureStates', (data) => {
      const featureStates: FeatureState[] = [];
      data.forEach((fs) => featureStates.push(new FeatureState(fs)));
      console.log(featureStates[0].Device.SerialNumber, 'received update', data);
      this._featuresStatesService.updateFeatureStates(featureStates);
    });
    this._hubConnection.on('SubscriberOutOfHomeActivities', (data: { SubscriberId: string; Activities: any[] }) => {
      this._appDataService.updateSubscriberOutOfHomeActivities(data.SubscriberId, data.Activities);
    });
  }

  public unregisterFromServerEvents(): void {
    if (!this._hubConnection) return;
    this._hubConnection.off('AlertsAndFeatureStates');
    this._hubConnection.off('SubscriberOutOfHomeActivities');
  }

  public stopConnection() : void {
    this.unregisterFromServerEvents();
    this._hubConnection.stop();
  }

  public isConnected(): boolean {
    return this._hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  public updateConnecedStatus() {
    this._connectionStatus$.next('connected');
  }
}
